import { Just, Nothing } from './maybe';
import { unreachable } from '../unreachable';
import type { Maybe } from './maybe';

export type Ok<T> = ['ok', T];
export type Error<E> = ['error', E];

export type Result<T, E> = Ok<T> | Error<E>;

export const ok = <T>(value: T): Ok<T> => ['ok', value];
export const error = <E>(err: E): Error<E> => ['error', err];

export const value = <T, E = any>(result: Result<T, E>): Maybe<T> =>
  result[0] === 'ok' ? Just(result[1]) : Nothing();
export const map =
  <T, E, R>(mapper: (t: T) => R) =>
  (result: Result<T, E>): Result<R, E> =>
    result[0] === 'error' ? result : ok(mapper(result[1]));
export const flatten = <T, E>(deep: Result<Result<T, E>, E>): Result<T, E> =>
  deep[0] === 'error' ? deep : deep[1];
export const flatMap =
  <T, E, R>(mapper: (t: T) => Result<R, E>) =>
  (result: Result<T, E>): Result<R, E> =>
    flatten<R, E>(map<T, E, Result<R, E>>(mapper)(result));
const _on =
  <T, E>(onOk: (value: T) => void, onError?: (err: E) => void) =>
  (result: Result<T, E>) => {
    switch (result[0]) {
      case 'ok':
        return onOk(result[1]);
      case 'error':
        return onError?.(result[1]);
      default:
        return unreachable(result);
    }
  };

export const on =
  <T, E>(onOk: (value: T) => void, onError?: (err: E) => void) =>
  (result: Result<T, E>): Result<T, E> => {
    _on(onOk, onError)(result);

    return result;
  };

export const Result = {
  ok,
  error,
  value,
  map,
  flatten,
  flatMap,
  on,
};
